import React from "react";
import Panel from "../../../common/components/panel";
import {
  ResourceMetadataFieldFragment,
  RmFieldLookupValueFragment,
  RmLookupAutoMap,
  RmLookupPriorityMappingTypeFragment,
  RmMappingDetailsFragment,
  RmProblemConnectionFragment,
  useResourceMetadataForFieldQuery,
  useRmMappingMoveLookupPriorityItemAfterMutation,
  useRmMappingSetLookupBasicTransformMutation,
  useRmMappingSetPrimarySourceMutation,
  useRmMappingUpdateLookupBasicMutation,
  useRmProblemAcknowledgeMutation,
} from "../../../graphql/generated";
import DescriptionListContainer from "../../../common/components/descriptionlistcontainer";
import {
  ResourceMetadataLookupValueComponent,
  resourceMetadataLookupValueForValue,
} from "../resource-metadata/resource-metadata-lookup-value-component";
import { ResourceMetadataFieldComponent } from "../resource-metadata/resource-metadata-field-component";
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  DropResult,
  Droppable,
  NotDraggingStyle,
} from "react-beautiful-dnd";
import { NotificationContext } from "../../../common/context/notification";
import { classNames } from "../../../common/utils/classnames";
import {
  LookupValueSelector,
  SelectedLookupValue,
  selectedValueSpread,
} from "./LookupValueSelector";
import LoadingIcon from "../../../common/components/loadingicon";
import CommonSwitch from "../../../common/components/switch";
import ProblemStatus from "../problem-status";
import { diceCoefficient } from "dice-coefficient";
import { EditableSource, sourceName } from "../mapping-source";
import { DescriptionList } from "../../../common/components/descriptionlist";
import { Link } from "react-router-dom";
import CanbySuggestions from "./CanbySuggestions";
import { LookupAutoMapSelector } from "../lookup-auto-map";

const RMLookupPriorityMappingTypeControl: React.FC<{
  mapping: RmMappingDetailsFragment;
  mappingType: RmLookupPriorityMappingTypeFragment;
  problems?: RmProblemConnectionFragment;
}> = ({ mapping, mappingType, problems }) => {
  const { notifier } = React.useContext(NotificationContext);
  const [source, setSource] = React.useState<string>("");
  const [destination, setDestination] = React.useState<SelectedLookupValue>({
    state: "ignore",
    newValue: "",
  });
  const [suggestionSetIndex, setSuggestionSetIndex] = React.useState(-1);

  const options = mapping.destinationIsLocalLookup
    ? mapping.field.localLookupValues
    : mapping.field.standardLookupValues;

  const [{ fetching }, mutation] =
    useRmMappingSetLookupBasicTransformMutation();
  const [{ fetching: updateFetching }, updateLookupBasicMutation] =
    useRmMappingUpdateLookupBasicMutation();
  const [{ fetching: setPrimarySourceFetching }, setPrimarySourceMutation] =
    useRmMappingSetPrimarySourceMutation();

  const setLookup = React.useCallback(async () => {
    if (fetching) {
      return;
    }
    await mutation({
      mappingId: mapping.id,
      source,
      ...selectedValueSpread(destination),
    }).then(notifier.notifyGraphql("Mapping updated"));
  }, [fetching, mutation, mapping, source, destination]);

  const onSplitOnCommaToggleChanged = React.useCallback(async () => {
    if (updateFetching) {
      return;
    }
    await updateLookupBasicMutation({
      mappingId: mapping.id,
      splitOnComma: !mappingType.splitOnComma,
      splitOnCommaInArray: mappingType.splitOnCommaInArray ?? false,
      autoMap: mappingType.autoMap,
    }).then(notifier.notifyGraphql("Split on commas toggled"));
  }, [
    mapping.id,
    mappingType.splitOnComma,
    mappingType.splitOnCommaInArray,
    updateFetching,
    updateLookupBasicMutation,
  ]);

  const onSplitOnCommaInArrayToggleChanged = React.useCallback(async () => {
    if (updateFetching) {
      return;
    }
    await updateLookupBasicMutation({
      mappingId: mapping.id,
      splitOnComma: mappingType.splitOnComma ?? false,
      splitOnCommaInArray: !mappingType.splitOnCommaInArray,
      autoMap: mappingType.autoMap,
    }).then(notifier.notifyGraphql("Split on commas in array toggled"));
  }, [
    mapping.id,
    mappingType.splitOnComma,
    mappingType.splitOnCommaInArray,
    updateFetching,
    updateLookupBasicMutation,
  ]);

  const onAutoMapChanged = React.useCallback(async (autoMap: RmLookupAutoMap) => {
    if (updateFetching) {
      return;
    }
    await updateLookupBasicMutation({
      mappingId: mapping.id,
      splitOnComma: mappingType.splitOnComma,
      splitOnCommaInArray: mappingType.splitOnCommaInArray,
      autoMap,
    }).then(notifier.notifyGraphql("Auto map updated"));
  }, [
    mapping.id,
    mappingType.splitOnComma,
    mappingType.splitOnCommaInArray,
    updateFetching,
    updateLookupBasicMutation,
  ]);

  const canbyMappedFields = React.useMemo(() => {
    const set: Set<string> = new Set();
    for (const transform of mappingType.transforms) {
      set.add(transform.from);
    }
    return set;
  }, [mappingType.transforms]);

  const canbyAdd = React.useCallback(
    async (source: string, destination: string) => {
      const { error } = await mutation({
        mappingId: mapping.id,
        source,
        destination,
        create: true,
      });
      if (error) {
        notifier.error(error);
        return false;
      } else {
        return true;
      }
    },
    [mapping.id],
  );

  const [{ fetching: moveItemFetching }, moveItemMutation] =
    useRmMappingMoveLookupPriorityItemAfterMutation();

  const anyMutationIsFetching = moveItemFetching || fetching || updateFetching;

  const onDragEnd = React.useCallback(
    async (result: DropResult) => {
      // Current update happening
      if (moveItemFetching) {
        return;
      }

      // dropped outside the list
      if (!result.destination) {
        return;
      }

      // Moved to the same place
      if (result.source.index == result.destination.index) {
        return;
      }

      let destinationIndex = result.destination.index;
      if (result.source.index < result.destination.index) {
        destinationIndex += 1;
      }

      const after =
        destinationIndex === 0
          ? null
          : mappingType.transforms[destinationIndex - 1].from;

      await moveItemMutation({
        mappingId: mapping.id,
        item: result.draggableId,
        after,
      }).then(notifier.notifyGraphql("Moved"));
    },
    [moveItemFetching, moveItemMutation, mapping, mappingType],
  );

  const [{ data: resourceMetadata }] = useResourceMetadataForFieldQuery({
    variables: {
      id: mapping.field.rootResource.resourceMetadataId,
      fieldName:
        mappingType.source.__typename === "RMFieldMappingSource"
          ? mappingType.source.name
          : "",
    },
    pause: mappingType.source.__typename !== "RMFieldMappingSource",
  });

  const resourceMetadataField =
    resourceMetadata?.resourceMetadata.field ?? undefined;

  function getTrStyle(
    isDragging: boolean,
    style: DraggingStyle | NotDraggingStyle | undefined,
  ): React.CSSProperties {
    if (isDragging) {
      return {
        display: "table",
        background: "rgba(255, 255, 255, 0.8)",
        ...style,
      };
    } else {
      return {
        ...style,
      };
    }
  }

  const filteredProblems = React.useMemo(
    () =>
      (problems?.edges ?? []).filter(
        (edge) =>
          edge.node.data["problem_type"] === "unidentified_lookup" ||
          edge.node.data["problem_type"] === "local_data_not_configured",
      ),
    [problems?.edges],
  );

  return (
    <>
      <Panel>
        <Panel.Title>Lookup Priority Mapping</Panel.Title>
        <Panel.Body>
          <DescriptionList>
            <DescriptionListContainer title="Source">
              <EditableSource
                source={mappingType.source}
                onSubmit={(_, sourceInput) =>
                  setPrimarySourceMutation({
                    mappingId: mapping.id,
                    source: sourceInput,
                  })
                }
                isSubmitting={setPrimarySourceFetching}
              />
            </DescriptionListContainer>
            <DescriptionListContainer
              title="Split on commas"
              info="Whether or not to treat a comma-separated string as multiple values. Enable this for RETS MLSes and certain API MLSes that return multiple values in a single comma-separated string."
            >
              <CommonSwitch
                label="Split on commas"
                enabled={mappingType.splitOnComma ?? false}
                toggle={onSplitOnCommaToggleChanged}
              />
            </DescriptionListContainer>
            <DescriptionListContainer
              title="Split on commas in array"
              info="Whether or not to split comma-separated values within array items. Enable this when array items contain comma-separated values that should be treated as multiple values."
            >
              <CommonSwitch
                label="Split on commas in array"
                enabled={mappingType.splitOnCommaInArray ?? false}
                toggle={onSplitOnCommaInArrayToggleChanged}
              />
            </DescriptionListContainer>
            {mapping.destinationIsLocalLookup && (<DescriptionListContainer
              title="Auto-map"
              info="How to auto-map previously unseen lookup values"
            >
              <LookupAutoMapSelector value={mappingType.autoMap} disabled={updateFetching} onChange={(v) => onAutoMapChanged(v)} />
            </DescriptionListContainer>)}
            <DescriptionListContainer title="Transforms" span={4}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="bruh">
                  {(provided) => (
                    <table
                      ref={provided.innerRef}
                      className={classNames(
                        "w-full text-sm text-left",
                        anyMutationIsFetching ? "opacity-25" : "",
                      )}
                    >
                      <thead>
                        <tr>
                          <th className="p-1">
                            Source:{" "}
                            <ResourceMetadataFieldComponent
                              source={mappingType.source}
                              field={resourceMetadataField}
                            />
                          </th>
                          <th className="p-1">
                            Destination: {mapping.field.name}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappingType.transforms.map((transform, idx) => (
                          <Draggable
                            key={transform.from}
                            draggableId={transform.from}
                            index={idx}
                            isDragDisabled={moveItemFetching}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getTrStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style,
                                )}
                                className="border-t"
                              >
                                <td className="p-1 w-1/2">
                                  ⣿{" "}
                                  <ResourceMetadataLookupValueComponent
                                    field={resourceMetadataField}
                                    value={transform.from}
                                  />
                                </td>
                                <td className="p-1 w-1/2">
                                  {transform.to && transform.to}
                                  {!transform.to && (
                                    <span className="text-gray-400">
                                      ignore
                                    </span>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </tbody>
                    </table>
                  )}
                </Droppable>
              </DragDropContext>
              <table
                className={classNames(
                  "w-full text-sm text-left",
                  anyMutationIsFetching ? "opacity-25" : "",
                )}
              >
                <tbody>
                  <tr key="add" className="border-t">
                    <td className="p-1 w-1/2">
                      <input
                        type="text"
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                      />
                    </td>
                    <td className="p-1 w-1/2">
                      <LookupValueSelector
                        selected={destination}
                        setSelected={setDestination}
                        options={options}
                        creationIsAllowed={mapping.destinationIsLocalLookup}
                      />{" "}
                      {!fetching && (
                        <button onClick={() => setLookup()}>Set</button>
                      )}
                      {fetching && <LoadingIcon />}
                    </td>
                  </tr>
                </tbody>
              </table>
            </DescriptionListContainer>
          </DescriptionList>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Title>Suggestions</Panel.Title>
        <Panel.Body>
          <table className="w-full text-sm text-left">
            <thead>
              <tr>
                <th className="p-1">
                  Source: {sourceName(mappingType.source)}
                </th>
                <th className="p-1">Destination: {mapping.field.name}</th>
                <th className="p-1">
                  {filteredProblems.length && (
                    <button
                      className="bg-zenlist-500 hover:bg-zenlist-700 text-white font-bold py-2 px-4 rounded"
                      onClick={() => setSuggestionSetIndex(0)}
                    >
                      Set all
                    </button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProblems.map((edge, idx) => (
                <Suggestion
                  key={edge.node.id}
                  edge={edge}
                  mapping={mapping}
                  field={resourceMetadataField}
                  options={options}
                  shouldTriggerSet={suggestionSetIndex === idx}
                  setSuggestionSetIndex={setSuggestionSetIndex}
                  isLast={idx === filteredProblems.length - 1}
                />
              ))}
            </tbody>
          </table>
        </Panel.Body>
      </Panel>

      {mapping.destinationIsLocalLookup && (
        <CanbySuggestions
          mapping={mapping}
          source={mappingType.source}
          existingMappings={canbyMappedFields}
          add={canbyAdd}
        />
      )}
    </>
  );
};

const Suggestion: React.FC<{
  edge: RmProblemConnectionFragment["edges"][number];
  mapping: RmMappingDetailsFragment;
  field?: ResourceMetadataFieldFragment;
  options: RmFieldLookupValueFragment[];
  isLast: boolean;
  setSuggestionSetIndex: React.Dispatch<React.SetStateAction<number>>;
  shouldTriggerSet: boolean;
}> = ({
  edge,
  mapping,
  field,
  options,
  isLast,
  setSuggestionSetIndex,
  shouldTriggerSet,
}) => {
    if (edge.node.data["problem_type"] === "unidentified_lookup") {
      return (
        <UnidentifiedLookupSuggestion
          edge={edge}
          mapping={mapping}
          field={field}
          options={options}
          isLast={isLast}
          setSuggestionSetIndex={setSuggestionSetIndex}
          shouldTriggerSet={shouldTriggerSet}
        />
      );
    }
    if (edge.node.data["problem_type"] === "local_data_not_configured") {
      return (
        <LocalDataNotConfiguredSuggestion
          edge={edge}
          mapping={mapping}
          isLast={isLast}
          setSuggestionSetIndex={setSuggestionSetIndex}
          shouldTriggerSet={shouldTriggerSet}
        />
      );
    }

    return <></>;
  };

const UnidentifiedLookupSuggestion: React.FC<{
  edge: RmProblemConnectionFragment["edges"][number];
  mapping: RmMappingDetailsFragment;
  field?: ResourceMetadataFieldFragment;
  options: RmFieldLookupValueFragment[];
  isLast: boolean;
  setSuggestionSetIndex: React.Dispatch<React.SetStateAction<number>>;
  shouldTriggerSet: boolean;
}> = ({
  edge,
  mapping,
  field,
  options,
  isLast,
  setSuggestionSetIndex,
  shouldTriggerSet,
}) => {
    const { notifier } = React.useContext(NotificationContext);

    if (edge.node.data["problem_type"] !== "unidentified_lookup") {
      return <></>;
    }

    const creationIsAllowed = mapping.destinationIsLocalLookup;
    const problem = edge.node;
    const source = problem.data["value"];
    const isAcknowledged = problem.acknowledgedAt != null;
    const lookupValue = resourceMetadataLookupValueForValue(source, field);

    const suggestions = options
      .map((value) => {
        return {
          value: value.value,
          definition: value.definition,
          coefficient: Math.max(
            diceCoefficient(source, value.value),
            lookupValue ? diceCoefficient(lookupValue.name, value.value) : 0.0,
          ),
        };
      })
      .filter((suggestion) => suggestion.coefficient > 0.5);

    suggestions.sort((a, b) => b.coefficient - a.coefficient);
    suggestions.splice(3);

    const [destination, setDestination] = React.useState<SelectedLookupValue>(
      () => {
        if (suggestions.length && suggestions[0].coefficient > 0.8) {
          return {
            state: "existing",
            selectedValue: suggestions[0].value,
            newValue: lookupValue?.name ?? source,
          };
        } else if (creationIsAllowed) {
          return {
            state: "new",
            newValue: lookupValue?.name ?? source,
          };
        } else {
          return {
            state: "ignore",
            newValue: "",
          };
        }
      },
    );

    const [{ fetching }, mutation] =
      useRmMappingSetLookupBasicTransformMutation();
    const [, mutationAcknowledge] = useRmProblemAcknowledgeMutation();

    const setLookup = React.useCallback(async () => {
      if (fetching) {
        return;
      }
      const { error } = await mutation({
        mappingId: mapping.id,
        source,
        ...selectedValueSpread(destination),
      }).then(notifier.notifyGraphql("Mapping updated"));
      if (!error) {
        mutationAcknowledge({
          problemId: problem.id,
        });
      }
    }, [
      fetching,
      mutation,
      mutationAcknowledge,
      mapping,
      problem,
      source,
      destination,
    ]);

    const setLookupFromSetAll = async () => {
      await setLookup();
      setSuggestionSetIndex((prevState) => (isLast ? -1 : prevState + 1));
    };

    React.useEffect(() => {
      if (shouldTriggerSet) setLookupFromSetAll();
    }, [shouldTriggerSet]);

    return (
      <tr>
        <td>
          <ResourceMetadataLookupValueComponent field={field} value={source} />
        </td>
        <td>
          <LookupValueSelector
            selected={destination}
            setSelected={setDestination}
            suggestions={suggestions}
            options={options}
            disabled={isAcknowledged}
            creationIsAllowed={creationIsAllowed}
          />
          {!fetching && (
            <button onClick={() => setLookup()} disabled={isAcknowledged}>
              Set
            </button>
          )}
          {fetching && <LoadingIcon />}
        </td>

        <td>
          <ProblemStatus problem={problem} />
        </td>
      </tr>
    );
  };

const LocalDataNotConfiguredSuggestion: React.FC<{
  edge: RmProblemConnectionFragment["edges"][number];
  mapping: RmMappingDetailsFragment;
  isLast: boolean;
  setSuggestionSetIndex: React.Dispatch<React.SetStateAction<number>>;
  shouldTriggerSet: boolean;
}> = ({ edge, mapping, isLast, setSuggestionSetIndex, shouldTriggerSet }) => {
  const problem = edge.node;
  const fieldName = mapping.field.name;

  React.useEffect(() => {
    if (shouldTriggerSet)
      setSuggestionSetIndex((prevState) => (isLast ? -1 : prevState + 1));
  }, [shouldTriggerSet]);

  if (fieldName === "LocalPropertyType") {
    return (
      <tr>
        <td>
          <strong>{problem.data["lookup_value"]}</strong> is configured on this
          field but is not configured as an MLS local property type.
        </td>
        <td>
          <Link
            to={`/resource_mapping/mlses/${problem.rootResource.mls.id}/property_types`}
            className="text-blue-900 hover:underline"
          >
            Configure property types
          </Link>
        </td>
        <td>
          <ProblemStatus problem={problem} />
        </td>
      </tr>
    );
  } else if (fieldName === "LocalStatus") {
    return (
      <tr>
        <td>
          <strong>{problem.data["lookup_value"]}</strong> is configured on this
          field but is not configured as an MLS local status.
        </td>
        <td>
          <Link
            to={`/resource_mapping/mlses/${problem.rootResource.mls.id}/statuses`}
            className="text-blue-900 hover:underline"
          >
            Configure statuses
          </Link>
        </td>
        <td>
          <ProblemStatus problem={problem} />
        </td>
      </tr>
    );
  } else {
    return <></>;
  }
};

export default RMLookupPriorityMappingTypeControl;
